import React from "react";
import { useDispatch } from "react-redux";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";

import { useFeatureFlag } from "../../hooks/useFeatureFlag.tsx";
import { voiceActions } from "../../store/voice.tsx";
import { unlockAudioPlayback } from "../../voice/audioPlayback.ts";
import { useVoiceStatus } from "../../voice/voiceStatus.ts";
import { voiceButtonSx } from "./voiceButtonSx.ts";
import { Trans } from "@lingui/react/macro";
import ComposerActionButton from "../search/ComposerActionButton.tsx";
import { VoiceModeIcon } from "../icons/ComposerIcons.tsx";

type Props = {
  disabled?: boolean;
  /** Renders the composer's inline action-row form: small, labelled, pill-shaped. */
  compact?: boolean;
};

/**
 * Enters hands-free voice mode.
 *
 * Distinct from VoiceRecorder, which dictates a single question into the text
 * field. This starts a session that keeps listening after each answer.
 */
const VoiceModeButton: React.FC<Props> = ({ disabled, compact }) => {
  const dispatch = useDispatch();
  // Voice-only mode is built on the same speech endpoints as the push-to-talk
  // recorder, and those are gated on `voice_assistant`. Requiring both avoids
  // handing someone a session that 403s on its first utterance.
  const isVoiceAssistantEnabled = useFeatureFlag("voice_assistant");
  const isVoiceOnlyEnabled = useFeatureFlag("voice_only_mode");
  const active = useVoiceStatus((status) => status.state !== "idle");

  if (!isVoiceAssistantEnabled || !isVoiceOnlyEnabled) return null;

  // A running session has its own controls (the orb, Stop and End).
  if (active) return null;

  const handleClick = () => {
    // Must run synchronously inside the gesture: this is the only user tap the
    // session gets, and every later utterance depends on the activation.
    unlockAudioPlayback();
    dispatch(voiceActions.requestVoiceMode());
  };

  const accessibleName = i18n._(
    msg({
      message: "Start voice mode",
      context:
        "Accessible name for the button that enters hands-free voice mode",
    }),
  );

  if (compact) {
    return (
      <Tooltip
        title={i18n._(
          msg({
            message: "Start voice mode",
            context: "Tooltip for the button that enters hands-free voice mode",
          }),
        )}
      >
        <span>
          <ComposerActionButton
            onClick={handleClick}
            disabled={disabled}
            aria-label={accessibleName}
            icon={<VoiceModeIcon />}
            label={
              <Trans context="Composer action that starts hands-free voice mode">
                Voice mode
              </Trans>
            }
          />
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      title={i18n._(
        msg({
          message: "Start voice mode",
          context: "Tooltip for the button that enters hands-free voice mode",
        }),
      )}
    >
      <span>
        <IconButton
          onClick={handleClick}
          disabled={disabled}
          aria-label={i18n._(
            msg({
              message: "Start voice mode",
              context:
                "Accessible name for the button that enters hands-free voice mode",
            }),
          )}
          sx={voiceButtonSx}
        >
          <RecordVoiceOverIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default VoiceModeButton;
